import { OS, determineOs } from '@disy/lib/determine-os';
import { on, platformModifierKey } from '@disy/lib/event-util';
import { logger } from '@disy/lib/logging';
import { supportsTouch } from '@disy/lib/media';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';
import { throttle } from '@disy/lib/throttle';

import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import { DATATABLE_DESTROYED, DATATABLE_INITIALIZED } from 'cadenza/base-table/base-table-event-types';
import { type DataView } from 'cadenza/workbook/data-view/data-view';
import { isScatterPlotChart } from 'cadenza/workbook/api/data-view-visualization-key';
import { setupScrollingForSmallViewportsOnDesktop } from 'cadenza/workbook/view/scroll-cover/small-viewport-wheel-interaction';
import { enableTwoFingerPan } from 'cadenza/workbook/view/scroll-cover/two-finger-pan-interaction';
import type { View } from 'cadenza/workbook/view/view';
import { getVisualisationKeyByViewId, hasDetailView } from 'cadenza/workbook/workbook-core-selectors.state';
import { isLinearLayout } from 'cadenza/workbook/worksheet-layout';

import './scroll-cover.css';
import i18n from './scroll-cover.properties';

const HINT_DISPLAY_DURATION = 3000;
const LISTENER_THROTTLE_DELAY = 50;

export class ScrollCover extends HTMLElement {
  readonly #view: View;
  #teardowns: Teardown[] = [];
  #documentScrollTeardown: Teardown | undefined;
  #viewSpecificTeardowns: Teardown[] = [];
  #tableInteractionTeardowns: Teardown[] = [];
  #viewTypeTeardown: Teardown[] = [];

  #hasTwoTouches: boolean = false;
  #modifierKeyPressed: boolean = false;
  #wasUsingTwoTouches: boolean = false;
  #wasUsingTwoTouchesTimeout: number | undefined;

  #chartHasZoom = false;
  #tableScrollBody: HTMLElement | null = null;

  constructor(view: View) {
    super();
    this.#view = view;
  }

  connectedCallback() {
    this.classList.add('d-scroll-cover', 'dark-bg');
    if (supportsTouch) {
      this.textContent = i18n('hint.mobile');
    } else {
      this.textContent = determineOs() === OS.MACOS ? i18n('hint.desktopMac') : i18n('hint.desktop');
    }
    this.hidden = true;
    this.#setupListeners();
    let visualizationKey = getVisualisationKeyByViewId(getStore().getState(), this.#view.viewId);
    // Reset the listeners when the view type changes - not all view types need special behavior.
    this.#viewTypeTeardown.push(
      observeStore(
        getStore(),
        (state) => getVisualisationKeyByViewId(state, this.#view.viewId),
        (vk) => {
          if (vk !== visualizationKey) {
            this.#teardowns = tearDown(this.#teardowns);
            this.#viewSpecificTeardowns = tearDown(this.#viewSpecificTeardowns);
            this.#tableInteractionTeardowns = tearDown(this.#tableInteractionTeardowns);
            this.#tableScrollBody = null;
            this.#setupListeners();
            visualizationKey = vk;
          }
        },
      ),
    );
  }

  disconnectedCallback() {
    this.#teardowns = tearDown(this.#teardowns);
    this.#documentScrollTeardown = tearDown(this.#documentScrollTeardown);
    this.#viewSpecificTeardowns = tearDown(this.#viewSpecificTeardowns);
    this.#tableInteractionTeardowns = tearDown(this.#tableInteractionTeardowns);
    this.#viewTypeTeardown = tearDown(this.#viewTypeTeardown);
  }

  #setupListeners() {
    if (viewNeedsToBeCoveredOnScroll(this.#view)) {
      if (supportsTouch) {
        this.#setupMobileListeners();
      } else {
        this.#setupDesktopListeners();
      }
      this.#setupViewTypeSpecificListeners();
      // Immediately hide the ScrollCover when the user opens the detail view of any view.
      observeStore(getStore(), hasDetailView, (result) => {
        if (result) {
          this.hidden = true;
        }
      });
    }
  }

  #setupMobileListeners() {
    this.#teardowns.push(
      on(
        this.#view,
        'touchmove',
        throttle((event) => {
          if (event.touches.length === 1) {
            this.#showScrollCoverIfApplicable();
          }
        }, LISTENER_THROTTLE_DELAY),
      ),
      on(
        this,
        'touchmove',
        (event) => {
          if (event.touches.length === 2) {
            event.preventDefault();
          }
        },
        { passive: false },
      ),
      on(document, ['touchstart', 'touchend', 'touchcancel'], (event: TouchEvent) => {
        if (this.#hasTwoTouches && event.touches.length < 2) {
          this.#wasUsingTwoTouches = true;
          clearTimeout(this.#wasUsingTwoTouchesTimeout);
          this.#wasUsingTwoTouchesTimeout = setTimeout(() => (this.#wasUsingTwoTouches = false), 500);
        }

        this.#hasTwoTouches = event.touches.length === 2;
      }),
    );
  }

  #setupViewTypeSpecificListeners() {
    if (this.#view.type === 'TABLE') {
      this.#viewSpecificTeardowns.push(
        on(this.#view, DATATABLE_INITIALIZED, () => {
          this.#setupTableScrollBody(true);
        }),
        on(this.#view, DATATABLE_DESTROYED, () => {
          this.#tableInteractionTeardowns = tearDown(this.#tableInteractionTeardowns);
          this.#tableScrollBody = null;
        }),
      );
      this.#setupTableScrollBody(false);
    }

    if (isScatterPlot(this.#view.dataViews)) {
      this.#viewSpecificTeardowns.push(
        on(this.#view, 'change:chart-zoom-settings', (event: CustomEvent) => {
          this.#chartHasZoom = Boolean(event.detail.hasZoom);
        }),
      );
    }
  }

  #setupTableScrollBody(logIfMissing: boolean) {
    const scrollBody = this.#view.querySelector<HTMLElement>('.dt-scroll-body');
    if (!scrollBody) {
      if (logIfMissing) {
        logger.error('scrollBody for table view not found.');
      }
      return;
    }

    if (scrollBody === this.#tableScrollBody) {
      return;
    }

    this.#tableInteractionTeardowns = tearDown(this.#tableInteractionTeardowns);
    if (supportsTouch) {
      this.#tableInteractionTeardowns.push(enableTwoFingerPan(scrollBody));
    } else {
      this.#tableInteractionTeardowns.push(setupScrollingForSmallViewportsOnDesktop(this.#view, scrollBody));
    }
    this.#tableScrollBody = scrollBody;
  }

  #setupDesktopListeners() {
    this.#teardowns.push(
      on(this.#view, 'mouseover', () => {
        if (!this.#documentScrollTeardown && isLinearLayout()) {
          this.#documentScrollTeardown = on(
            document,
            'scroll',
            throttle(() => {
              this.#showScrollCoverIfApplicable();
            }),
          );
        }
      }),
      on(this.#view, 'mouseout', () => {
        this.#documentScrollTeardown = tearDown(this.#documentScrollTeardown);
      }),
      on(document, ['keydown', 'keyup'], (event: KeyboardEvent) => {
        this.#modifierKeyPressed = platformModifierKey(event);
        if (this.#modifierKeyPressed) {
          // Immediately hide the ScrollCover when the user presses the modifier key.
          this.hidden = true;
        }
      }),
    );
  }

  #showScrollCoverIfApplicable() {
    const linearLayoutWithTwoTouchPoints = supportsTouch && isLinearLayout() && this.#hasTwoTouches;
    if (
      linearLayoutWithTwoTouchPoints ||
      this.#modifierKeyPressed ||
      !this.hidden ||
      hasDetailView(getStore().getState()) ||
      this.#wasUsingTwoTouches ||
      isTableAndNoScrolling(this.#tableScrollBody) ||
      (isScatterPlot(this.#view.dataViews) && !this.#chartHasZoom) ||
      this.#view.querySelector<HTMLElement>('.d-view-cover')?.hidden === false
    ) {
      // Never show the ScrollCover when
      // 1. linear layout is active on mobile, and the user has two fingers on the screen.
      // 2. the modifier key is pressed (e.g., CTRL on desktop).
      // 3. the ScrollCover is already visible: finish the currently running timeout first to not add multiple timeouts.
      // 4. a view is in detail view.
      // 5. the user was just using two fingers before.
      // 6. #tableScrollBody is present but has no scrolling.
      // 7. the #view hosts a scatter chart, and the chart has no zoom.
      // 8. the view cover is visible (e.g., the view is not yet fully configured).
      return;
    }

    this.hidden = false;
    setTimeout(() => (this.hidden = true), HINT_DISPLAY_DURATION);
  }
}

function viewNeedsToBeCoveredOnScroll(view: View) {
  const isTableOrMap = view.type === 'TABLE' || view.type === 'MAP';
  return isTableOrMap || isScatterPlot(view.dataViews);
}

customElements.define('d-scroll-cover', ScrollCover);

function isScatterPlot(dataViews: DataView[]) {
  return Boolean(dataViews.length) && dataViews.every((dataView) => isScatterPlotChart(dataView.visualizationKey));
}

function isTableAndNoScrolling(scrollBody: HTMLElement | null) {
  if (!scrollBody) {
    return false;
  }
  const hasHorizontalScrollbar = scrollBody.scrollWidth > scrollBody.clientWidth;
  const hasVerticalScrollbar = scrollBody.scrollHeight > scrollBody.clientHeight;
  return !hasHorizontalScrollbar && !hasVerticalScrollbar;
}
